/** Años hacia delante que el calendario permite seleccionar (FR-006 —
 * specs/015-agenda-listado). */
const YEARS_AHEAD = 3

/** Elimina la parte horaria de una fecha (comparación por día de calendario local,
 * no por instante — evita falsos negativos por horas/minutos). */
export function stripTime(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

/** Medianoche local de "hoy". */
export function getToday(): Date {
  return stripTime(new Date())
}

/** Última fecha seleccionable: `from` + 3 años (por defecto, hoy + 3 años). */
export function getMaxDate(from: Date = getToday()): Date {
  return new Date(from.getFullYear() + YEARS_AHEAD, from.getMonth(), from.getDate())
}

/** `true` si `date` cae dentro de `[min, max]`, ambos inclusive (comparación por día
 * de calendario). */
export function isDateInRange(date: Date, min: Date, max: Date): boolean {
  const time = stripTime(date).getTime()
  return time >= stripTime(min).getTime() && time <= stripTime(max).getTime()
}

/**
 * Parsea `YYYY-MM-DD` como fecha local (no UTC) — `new Date('YYYY-MM-DD')`
 * interpreta la cadena como medianoche UTC, lo que se desplaza al día anterior en
 * husos horarios negativos. Devuelve `null` si el formato no es válido o la fecha
 * no existe de verdad (p. ej. `2026-02-30`, que `Date` normalmente "arregla"
 * desbordando a marzo — aquí se rechaza en vez de aceptarla silenciosamente).
 */
export function parseIsoDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null
  const [, yearStr, monthStr, dayStr] = match
  const year = Number(yearStr)
  const month = Number(monthStr)
  const day = Number(dayStr)
  const date = new Date(year, month - 1, day)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null
  return date
}

/** Formatea una fecha local como `YYYY-MM-DD` (mismo formato que los parámetros
 * `start_at`/`end_at` de `/data?slug=agenda`). */
export function toIsoDate(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** `true` si `a` y `b` caen en el mismo día de calendario (compara año/mes/día
 * local, ignora la hora). */
export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

/**
 * Selección de una fecha única (`end: null`) o de un rango de dos fechas
 * (`start`/`end`, ambas inclusive) — modelo compartido por `DayStrip`,
 * `Calendar` y `DatePicker` para la selección de "múltiples fechas" al estilo
 * reserva de vuelos (fiel al diseño de referencia, `eci-firefly-uikit`,
 * `shared/DateStrip/dateRange.ts`). `start` nunca es posterior a `end` — lo
 * garantiza `pickDate`, la única función que produce un `DateRange` nuevo.
 */
export interface DateRange {
  readonly start: Date
  readonly end: Date | null
}

/**
 * Reduce un click sobre `clicked` contra el `DateRange` vigente, con la
 * semántica de dos clicks del diseño de referencia:
 * - Con una única fecha activa (`end === null`): el click cierra el rango
 *   (`end` deja de ser `null`), reordenando `start`/`end` si `clicked` es
 *   anterior a `start` — nunca se produce un rango invertido. Click sobre la
 *   propia fecha ya activa: no-op (el rango no cambia).
 * - Con un rango ya completo (`end !== null`): el click reinicia la selección
 *   a una fecha única (`{ start: clicked, end: null }`) — el siguiente click
 *   volverá a cerrar un rango nuevo desde ahí.
 */
export function pickDate(current: DateRange, clicked: Date): DateRange {
  if (current.end === null) {
    if (isSameDay(clicked, current.start)) return current
    return clicked < current.start ? { start: clicked, end: current.start } : { start: current.start, end: clicked }
  }
  return { start: clicked, end: null }
}

/** `true` si `date` cae ESTRICTAMENTE entre `start` y `end` (ninguno de los dos
 * extremos, esos los resuelve `isRangeEndpoint`) — `false` con una única fecha
 * activa (`end === null`, no hay "interior" que resaltar). */
export function isInRange(date: Date, range: DateRange): boolean {
  if (!range.end) return false
  const time = stripTime(date).getTime()
  return time > stripTime(range.start).getTime() && time < stripTime(range.end).getTime()
}

/** `true` si `date` es alguno de los extremos del rango (la única fecha activa
 * cuando `end === null`, o el inicio/fin cuando ya está completo). */
export function isRangeEndpoint(date: Date, range: DateRange): boolean {
  return isSameDay(date, range.start) || (range.end !== null && isSameDay(date, range.end))
}
